import React, { useContext, useEffect, useState } from 'react';
import { SessionContext } from '../contexts/session-context';
import {
  UserPreferenceContext,
  UserPreferenceContextType,
} from '../contexts/user-preference-context';
import { UserPreference } from '../models/user-preferences.model';
import UserPreferenceService from '../services/user-preference.service';
import VersionService from '@services/version.service';
import { ApplicationVersion } from '@models/application-version.model';
import ApplicationService from '@services/application.service';
import { produce } from 'immer';
import ProjectService from '@services/project.service';
import { Project } from '@models/project.model';
import { Application } from '@models/application.model';
import { getAllUserForActivity } from '@services/user.service';
import { UserActivity } from '@models/user-activity.model';
import { useNavigate } from 'react-router-dom';
import { ApplicationType } from '@testsigmainc/constants';
import { logErrorToSentry } from '@utils/common';

export default function UserPreferenceContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [userPreference, setUserPreference] = useState<
    UserPreference | undefined
  >(undefined);
  const sessionContext = useContext(SessionContext);
  const user = sessionContext?.session?.user;
  const [availableUsers, setAvailableUsers] = useState<UserActivity[]>([]);
  const [availableUsersMap, setAvailableUsersMap] = useState(new Map());
  const [userProjectIds, setUserProjectIds] = useState([]);
  const navigate = useNavigate();
  const { data } = getAllUserForActivity();

  useEffect(() => {
    const localIsBulkActionOngoing = JSON.parse(
      localStorage.getItem('isBulkActionOngoing'),
    );
    const localBulkActionId = Number(localStorage.getItem('bulkActionId'));
    UserPreferenceService.show(user.id).then((res) => {
      if (res instanceof UserPreference) {
        res.isBulkActionOngoing = localIsBulkActionOngoing || false;
        if (localIsBulkActionOngoing) {
          res.bulkActionId = localBulkActionId;
        }
        setUserPreference(res);
      }
    });
  }, []);

  useEffect(() => {
    if (data) {
      setAvailableUsers(data.content);
    }
  }, [data]);

  useEffect(() => {
    if (!availableUsers) return;
    setAvailableUsersMap(
      new Map(availableUsers.map((user) => [user.id, user])),
    );
  }, [availableUsers]);

  const setOrUpdatePreference = (
    userPreference: UserPreference,
    callback: VoidFunction,
  ) => {
    UserPreferenceService.save(userPreference).then((res) => {
      if (res instanceof UserPreference) {
        const { selectedApplication, selectedProject, selectedVersion } =
          userPreference.preference.projectPreference;

        const updatedProjectPreference = produce(
          res.preference.projectPreference,
          (draft) => {
            const projectPref = draft;
            Object.assign(projectPref, {
              selectedApplication,
              selectedProject,
              selectedVersion,
            });
          },
        );
        userPreference.preference.projectPreference = updatedProjectPreference;
        setUserPreference(userPreference);
      }
      if (callback) {
        callback();
      }
    });
  };

  const setSelectedProject = (project: Project) => {
    const updatedProjectPreference = produce(
      userPreference.preference.projectPreference,
      (draft) => {
        draft.selectedProject = project;
      },
    );
    userPreference.preference.projectPreference = updatedProjectPreference;
    setUserPreference(userPreference);
  };

  const setSelectedApplication = (application: Application) => {
    const updatedProjectPreference = produce(
      userPreference.preference.projectPreference,
      (draft) => {
        draft.selectedApplication = application;
      },
    );
    userPreference.preference.projectPreference = updatedProjectPreference;
    setUserPreference(userPreference);
  };

  const setSelectedVersion = (version: ApplicationVersion) => {
    const updatedProjectPreference = produce(
      userPreference.preference.projectPreference,
      (draft) => {
        draft.selectedVersion = version;
      },
    );
    userPreference.preference.projectPreference = updatedProjectPreference;
    setUserPreference(userPreference);
  };

  const fetchVersionToUpdatePreference = (props: {
    versionId: number;
    savePreference?: boolean;
    callback?: (version: ApplicationVersion) => void;
  }) => {
    const {
      versionId,
      savePreference = false,
      callback = (version) => {},
    } = props;

    VersionService.show(Number(versionId))
      .then((res: ApplicationVersion) => {
        const updatedProjectPreference = produce(
          userPreference.preference.projectPreference,
          (draft) => {
            const { application, id, applicationId } = res;
            Object.assign(draft, {
              selectedVersion: res,
              selectedApplication: application,
              selectedProject: application?.project,
              versionId: id,
              applicationId: applicationId,
              projectId: application?.projectId,
            });
          },
        );
        userPreference.preference.projectPreference = updatedProjectPreference;
        userPreference.preference.testCaseReportFilterDetails = null;

        if (savePreference) {
          return setOrUpdatePreference(userPreference, () => callback(res));
        }

        setUserPreference(userPreference);
        callback(res);
      })
      .catch(console.error);
  };

  //sets user preference to first application and it's first version
  const updateDefaultForProjectId = async (props: {
    projectId?: number;
    applicationId?: number;
    callback?: (version: ApplicationVersion) => void;
  }) => {
    let { projectId, applicationId, callback = (version) => {} } = props;
    try {
      let firstApplication = null;
      let firstVersion = null;
      if (applicationId || applicationId === 0) {
        const versions = await VersionService.findAll(
          `applicationId:${applicationId}`,
        );
        firstVersion = versions?.content?.[0];
        firstApplication = firstVersion.application;
      } else {
        if (!projectId && projectId !== 0) {
          const userId = sessionContext?.session?.user?.id;
          const projects = await ProjectService.findAll(`userId:${userId}`);
          projectId = projects?.content?.[0]?.id;
        }
        const applications = await ApplicationService.findAll(
          `projectId:${projectId}`,
        );
        firstApplication = applications?.content?.[0];

        const versions = await VersionService.findAll(
          `applicationId:${firstApplication.id}`,
        );
        firstVersion = versions?.content?.[0];
      }

      const updatedProjectPreference = produce(
        userPreference.preference.projectPreference,
        (draft) => {
          Object.assign(draft, {
            selectedProject: firstApplication?.project,
            projectId: firstApplication?.projectId,
            selectedApplication: firstApplication,
            applicationId: firstApplication.id,
            selectedVersion: firstVersion,
            versionId: firstVersion?.id,
          });
        },
      );

      userPreference.preference.projectPreference = updatedProjectPreference;

      setOrUpdatePreference(userPreference, () => callback(firstVersion));
      const applicationType = firstVersion.application?.applicationType;

      if (applicationType === ApplicationType.WINDOWS_ADVANCED) {
        navigate('/agents');
        return sessionContext.refreshSession();
      }

      if (applicationType === ApplicationType.Salesforce) {
        navigate(`/salesforce/${firstApplication?.projectId}/connection`);
        return sessionContext.refreshSession();
      }

      navigate(`/td/${firstVersion.id}/cases`);
    } catch (error) {
      logErrorToSentry({ error });
    }
  };

  return (
    <>
      {userPreference && (
        <UserPreferenceContext.Provider
          value={
            {
              userPreference,
              setOrUpdatePreference,
              updatePreferenceContext: setUserPreference,
              setUserProjectIds,
              userProjectIds,
              fetchVersionToUpdatePreference,
              updateDefaultForProjectId,
              setSelectedProject,
              setSelectedApplication,
              setSelectedVersion,
              availableUsers,
              availableUsersMap,
            } as UserPreferenceContextType
          }
        >
          {children}
        </UserPreferenceContext.Provider>
      )}
    </>
  );
}
